import { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { AlertTriangle, BookOpen, FileDown, Printer, Users } from 'lucide-react';
import ConfigurableUniformSvg from '../../../src/components/uniforms/ConfigurableUniformSvg.jsx';
import ProfessionalAttireSvg from '../../../src/components/uniforms/ProfessionalAttireSvg.jsx';
import ParamedicUniformSvg from '../../../src/components/uniforms/ParamedicUniformSvg.jsx';
import { getEasyReadRoleCopy } from '../../../src/lib/easyReadRolePresentation.js';
import { getPlainUniformDescription } from '../lib/easyReadPrintUniforms';
import { getEasyReadStageRows } from '../../../src/lib/easyReadVisualRows.js';
import EasyReadVisual from './StudioEasyReadVisual.jsx';
import { resolveEasyReadPrintUniform } from '../lib/easyReadPrintUniforms';
import { loadGenericUniforms } from '../lib/genericUniformApi';
import { visibleTrusts } from '../lib/accessApi';
import { assertPrintEntitlement } from '../lib/printEntitlements';
import {
  EASY_READ_CONFIGURABLE_JOURNEYS,
  getEasyReadJourneySource,
} from '../../../src/data/configurableJourneyLibrary.js';
import './easyReadPrint.css';

const DEFAULT_ID = 'easy-read-blood-test';
function PrintRoleIllustration({ uniform }) {
  let illustration = <Users size={44} strokeWidth={1.6} />;
  if (uniform.kind === 'professional') {
    illustration = <ProfessionalAttireSvg className="er-role-uniform" />;
  } else if (uniform.kind === 'paramedic') {
    illustration = <ParamedicUniformSvg className="er-role-uniform" />;
  } else if (uniform.kind === 'garment') {
    illustration = <ConfigurableUniformSvg
      templateId={uniform.templateId}
      body={uniform.body}
      trim={uniform.trim}
      showBadge
      provider="nhs"
      className="er-role-uniform"
    />;
  }
  return <span className="er-role-image" aria-hidden="true">{illustration}</span>;
}

function PrintablePage({ children, className = '' }) {
  return <section className={`er-print-page ${className}`}>{children}</section>;
}

function EvaluationStamp() {
  return <p className="er-evaluation-stamp">EVALUATION DRAFT — NOT PATIENT INSTRUCTIONS</p>;
}

function PageFooter() {
  return <footer className="er-print-footer">Know Your Care® · Easy Read evaluation copy · Follow advice from your own hospital team.</footer>;
}

export default function EasyReadPrintStudio({ workspaceSwitcher = null, studioAccess = null, studioTrusts = [] }) {
  const scopedTrusts = visibleTrusts(studioAccess, studioTrusts).filter(trust =>
    trust.active !== false && Array.isArray(trust.easyReadPdfJourneyIds) && trust.easyReadPdfJourneyIds.length > 0);
  const [trustId, setTrustId] = useState(scopedTrusts[0]?.id || '');
  const selectedTrust = scopedTrusts.find(trust => trust.id === trustId) || scopedTrusts[0] || null;
  const availableJourneys = EASY_READ_CONFIGURABLE_JOURNEYS.filter(item =>
    selectedTrust?.easyReadPdfJourneyIds?.includes(item.id));
  const [selectedId, setSelectedId] = useState(DEFAULT_ID);
  const effectiveSelectedId = availableJourneys.find(item => item.id === selectedId)?.id || availableJourneys[0]?.id || '';
  const [includeQuestions, setIncludeQuestions] = useState(true);
  const [notice, setNotice] = useState('');
  const [publishedUniforms, setPublishedUniforms] = useState(null);
  const [uniformStatus, setUniformStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    loadGenericUniforms().then((payload) => {
      if (cancelled) return;
      setPublishedUniforms(payload.uniforms || {});
      setUniformStatus('ready');
    }).catch(() => {
      if (!cancelled) setUniformStatus('error');
    });
    return () => { cancelled = true; };
  }, []);
  const journey = useMemo(() => getEasyReadJourneySource(effectiveSelectedId), [effectiveSelectedId]);
  const stages = journey?.stages || [];
  const roles = journey?.roles || [];
  const questions = journey?.questions || [];
  const rolePages = Array.from({ length: Math.ceil(roles.length / 3) }, (_, index) => roles.slice(index * 3, (index + 1) * 3));

  async function printPdf() {
    setNotice('');
    try {
      await assertPrintEntitlement(selectedTrust?.id, effectiveSelectedId, 'easy-read');
    } catch (error) {
      setNotice(error.message || 'This Easy Read Journey is not included in the Trust agreement.');
      return;
    }
    try {
      // Re-read immediately before printing. flushSync ensures that refreshed
      // uniforms reach the printable DOM before the image readiness check.
      const payload = await loadGenericUniforms();
      flushSync(() => {
        setPublishedUniforms(payload.uniforms || {});
        setUniformStatus('ready');
      });
      const images = [...document.querySelectorAll('.er-print-document img')];
      await Promise.all(images.map(async (img) => {
        if (!img.complete) {
          await new Promise((resolve, reject) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', reject, { once: true });
          });
        }
        if (!img.naturalWidth) throw new Error('An Easy Read illustration could not be loaded.');
        if (img.decode) await img.decode().catch(() => undefined);
      }));
      window.print();
    } catch (error) {
      setUniformStatus('error');
      setNotice(`Printing stopped: the latest Generic NHS uniforms or images could not be confirmed. ${error.message || 'Please try again.'}`);
    }
  }

  if (!journey || !selectedTrust) return <main className="access-state"><h1>No contracted Easy Read Journeys</h1><p>Easy Read PDFs are separate from Standard and Large Text Journeys and must be explicitly included in the Trust agreement by a Platform Admin.</p></main>;

  return <div className="app-shell er-print-studio">
    <header className="topbar">
      <div className="brand"><span className="brand-mark"><BookOpen /></span><div><strong>Know Your Care<sup className="registered-mark">®</sup> Studio</strong><small>Easy Read print evaluation</small></div></div>
    </header>
    <div className="workspace">
      <aside className="catalogue">
        {workspaceSwitcher}
        <div className="catalogue-intro">
          <p className="eyebrow">Platform Admin · Evaluation only</p>
          <h1>Easy Read PDFs</h1>
          <p>Preview and print the existing Easy Read Journeys for supported review with patients and learning-disability groups.</p>
        </div>
      </aside>
      <main className="review-workspace">
        <section className="trust-config-panel er-print-controls">
          <div className="trust-panel-heading">
            <div>
              <p className="eyebrow">Print preview</p>
              <h2>Choose an Easy Read Journey</h2>
              <p>Each instruction has a picture cue on the left and larger, more widely spaced text. Only Easy Read Journeys separately included in the Trust agreement are available.</p>
            </div>
          </div>
          <div className="er-print-notice" role="note"><AlertTriangle size={20} /><span>Picture cues are draft pictograms, not real hospital photos. All exports are evaluation drafts. Review wording and imagery with people who have learning disabilities before use. No Trust is published and patient access remains unchanged.</span></div>
          <div className="er-print-options">
            <label>Trust / site<select value={selectedTrust.id} onChange={event => { setTrustId(event.target.value); setSelectedId(''); }}>{scopedTrusts.map(trust => <option key={trust.id} value={trust.id}>{trust.shortName || trust.name}</option>)}</select></label>
            <label>Easy Read Journey
              <select value={effectiveSelectedId} onChange={(event) => setSelectedId(event.target.value)}>
                {availableJourneys.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
              </select>
            </label>
            <label className="er-print-checkbox"><input type="checkbox" checked={includeQuestions} onChange={(event) => setIncludeQuestions(event.target.checked)} /> Include suggested questions</label>
            <button className="button primary" type="button" onClick={printPdf}><Printer size={17} /> Print / Save as PDF</button>
          </div>
          <p className="er-print-help"><FileDown size={16} /> In your browser's print window choose <strong>Save as PDF</strong>, A4 portrait, and print backgrounds if available. No document is uploaded to a server.</p>
          <p className="er-print-help" role="status">{uniformStatus === 'ready' ? 'Staff clothing comes from the published Generic NHS uniform settings. It may differ from the clothing used by your hospital.' : uniformStatus === 'loading' ? 'Loading published Generic NHS uniforms…' : 'Could not load the current Generic NHS uniforms. The preview shows bundled defaults; printing will retry and will stop if these cannot be checked.'}</p>
          {notice && <p className="studio-notice" role="alert">{notice}</p>}
        </section>

        <section className="er-screen-preview" aria-label="Printable Easy Read document preview">
          <div className="er-preview-heading"><strong>Document preview</strong><span>{stages.length} stages · English source · Evaluation draft</span></div>
          <div className="er-print-document">
            <PrintablePage className="er-cover">
              <EvaluationStamp />
              <div className="er-print-brand">Know Your Care®</div>
              <p className="er-print-eyebrow">Easy Read Hospital Journey</p>
              <h1>{journey.title.replace(': Easy Read version', '')}</h1>
              <p className="er-print-lede">{journey.introduction}</p>
              {stages[0]?.easyReadImage?.src && <img src={stages[0].easyReadImage.src} alt={stages[0].easyReadImage.alt || ''} className="er-cover-image" />}
              <p className="er-cover-support">You can read this guide with someone you trust.</p>
              <PageFooter />
            </PrintablePage>

            {stages.map((stage, index) => {
              const rows = getEasyReadStageRows(journey, stage);
              return <PrintablePage key={stage.id} className="er-stage-page">
                <EvaluationStamp />
                <p className="er-print-eyebrow">Step {index + 1} of {stages.length}</p>
                <h2>{stage.title}</h2>
                <div className="er-stage-rows">
                  {rows.map((row) => <div className="er-stage-row" key={row.id}>
                    <EasyReadVisual row={row} className="er-stage-picture" iconClassName="er-stage-pictogram" />
                    <p>{row.text}</p>
                  </div>)}
                </div>
                <PageFooter />
              </PrintablePage>;
            })}

            {rolePages.map((pageRoles, pageIndex) => <PrintablePage key={pageIndex} className="er-extras-page er-staff-page">
              <EvaluationStamp />
              <h2>People you might meet</h2>
              <p className="er-staff-note">Staff clothes may look different at your hospital.</p>
              <div className="er-role-list">{pageRoles.map((role, index) => {
                const copy = getEasyReadRoleCopy(role, roles);
                const uniform = resolveEasyReadPrintUniform(role, publishedUniforms);
                return <div className="er-role" key={role.id || index}>
                  <PrintRoleIllustration uniform={uniform} />
                  <div><strong>{copy.title}</strong><p>{copy.description}</p><p className="er-role-uniform-description"><strong>Clothes:</strong> {getPlainUniformDescription(uniform)}</p></div>
                </div>;
              })}</div>
              <PageFooter />
            </PrintablePage>)}

            {includeQuestions && questions.length > 0 && <PrintablePage className="er-extras-page">
              <EvaluationStamp />
              <h2>Questions you can ask</h2>
              <div className="er-question-list">{questions.map((question, index) => <p key={index}>{question}</p>)}</div>
              <PageFooter />
            </PrintablePage>}

            <PrintablePage className="er-extras-page er-notes-page">
              <EvaluationStamp />
              <h2>Questions to ask my team</h2>
              <p className="er-writing-prompt">Write down questions you want to ask.</p>
              <div className="er-writing-lines" aria-label="Space to write questions">{Array.from({ length: 5 }, (_, index) => <div key={index} />)}</div>
              <h2 className="er-notes-heading">Notes to remember</h2>
              <p className="er-writing-prompt">Write down dates, instructions or other notes.</p>
              <div className="er-writing-lines er-writing-lines-large" aria-label="Space to write notes">{Array.from({ length: 8 }, (_, index) => <div key={index} />)}</div>
              <PageFooter />
            </PrintablePage>

            <PrintablePage className="er-finish-page">
              <EvaluationStamp />
              <h2>Remember</h2>
              <p className="er-finish-message">{journey.disclaimer}</p>
              <p className="er-finish-message">You can ask hospital staff to explain something again.</p>
              <PageFooter />
            </PrintablePage>
          </div>
        </section>
      </main>
    </div>
  </div>;
}
